import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { HardHat, ArrowLeft, UserCircle } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

export default function StaffLoginPage() {
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) {
      toast.error("Fill all fields");
      return;
    }
    setLoading(true);
    try {
      const user = await login(loginEmail, loginPassword);
      if (user.role === "agent") {
        toast.success("Welcome back!");
        navigate("/agent");
        setLoading(false);
        return;
      }
      logout();
      if (user.role === "admin") {
        toast.error("Admins should use the admin sign-in page.");
      } else {
        toast.error("Use the supplier portal for supplier accounts.");
      }
      setLoading(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="text-neutral-500 hover:text-neutral-900"
            data-testid="staff-login-back-home"
          >
            <ArrowLeft size={20} weight="bold" />
          </button>
          <HardHat size={28} weight="bold" className="text-violet-600" />
          <span className="text-xl font-bold tracking-tight">SCAFFOINDIA</span>
          <Badge className="rounded-sm bg-violet-100 text-violet-900 border border-violet-300 text-[10px] shrink-0">STAFF</Badge>
        </div>

        <Card className="rounded-sm border-violet-200 shadow-[4px_4px_0px_#0A0A0A]">
          <CardHeader className="border-b border-neutral-200">
            <div className="flex items-center gap-2">
              <UserCircle size={22} className="text-violet-600" weight="bold" />
              <CardTitle className="text-lg font-bold">Staff sign in</CardTitle>
            </div>
            <p className="text-sm text-neutral-500 font-normal pt-1">
              For internal team members assigned to leads. Your account is created by an administrator.
            </p>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Email</label>
              <Input
                data-testid="staff-login-email"
                type="email"
                placeholder="your@email.com"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                className="rounded-sm border-neutral-300"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-1 block">Password</label>
              <Input
                data-testid="staff-login-password"
                type="password"
                placeholder="Enter password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="rounded-sm border-neutral-300"
                onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              />
            </div>
            <Button
              data-testid="staff-login-submit"
              onClick={handleLogin}
              disabled={loading}
              className="w-full bg-violet-700 hover:bg-violet-800 text-white rounded-sm h-11 font-bold"
            >
              {loading ? "Signing in..." : "Sign in to dashboard"}
            </Button>
            <p className="text-sm text-center text-neutral-600 pt-2 border-t border-neutral-100">
              Supplier?{" "}
              <Link to="/supplier/login" className="text-orange-600 font-semibold hover:underline" data-testid="link-supplier-from-staff">
                Supplier portal
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
